import { ActionIcon, Badge, Group, Table, Text, Tooltip } from "@mantine/core";
import { IconEdit, IconPower } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import type { ReflexDefinition } from "../../../services/admin/reflexes.service";

interface Props {
  reflex: ReflexDefinition;
  onEdit: (reflex: ReflexDefinition) => void;
  onToggle: (reflex: ReflexDefinition) => void;
}

export function ReflexAdminRow({ reflex, onEdit, onToggle }: Props) {
  const handleEdit = useCallback(() => onEdit(reflex), [onEdit, reflex]);
  const handleToggle = useCallback(() => onToggle(reflex), [onToggle, reflex]);
  const summary = useMemo(
    () =>
      reflex.rules
        .map(
          (rule) =>
            `${rule.targetPanel.name}: ${rule.icdCodes
              .map((code) => code.code)
              .join(", ")}`
        )
        .join(" • "),
    [reflex.rules]
  );
  return (
    <Table.Tr opacity={reflex.isActive ? 1 : 0.65}>
      <Table.Td>
        <Text fw={600}>{reflex.name}</Text>
        {reflex.description && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {reflex.description}
          </Text>
        )}
      </Table.Td>
      <Table.Td>{reflex.startingPanel.name}</Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={2}>
          {summary}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light">v{reflex.versionNumber}</Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={reflex.isActive ? "green" : "gray"}>
          {reflex.isActive ? "Active" : "Inactive"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end" wrap="nowrap">
          <Tooltip label="Create a new version">
            <ActionIcon
              size={44}
              variant="subtle"
              aria-label={`Edit ${reflex.name}`}
              onClick={handleEdit}
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={reflex.isActive ? "Deactivate" : "Activate"}>
            <ActionIcon
              size={44}
              variant="subtle"
              color={reflex.isActive ? "orange" : "blue"}
              aria-label={`${reflex.isActive ? "Deactivate" : "Activate"} ${
                reflex.name
              }`}
              onClick={handleToggle}
            >
              <IconPower size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
